if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');
        this.pendingUpdates = {};
				this.flushQueueTimer = null;

        const debouncedOnChange = debounce((event) => {
          const input = event.target;
          const originalValue = parseInt(input.value, 10);
          this.capQuantityInput(input, originalValue);

          if (Number.isNaN(originalValue) || originalValue === 0) {
            this.startQueue(input.dataset.index, 0);
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);
        

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'quick-add') return;

          const variantId = parseInt(this.dataset.index, 10);
          const items = event?.cartData?.items;

          if (Array.isArray(items)) {
            this.syncQuantityFromCart(event.cartData, variantId);
            this.listenForActiveInput();
            this.listenForKeydown();
            return;
          }

          const eventVariantId = Number(event.cartData?.variant_id || event.productVariantId);
          if (eventVariantId && eventVariantId !== variantId) return;

          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      syncQuantityFromCart(cartData, variantId = parseInt(this.dataset.index, 10)) {
        const input = this.input;
        if (!input || !Array.isArray(cartData?.items)) return false;

        const cartLine = cartData.items.find((item) => {
          const itemVariantId = Number(item.variant_id ?? item.id);
          return itemVariantId === variantId;
        });
        const actualQty = cartLine ? Number(cartLine.quantity) || 0 : 0;
        const currentQty = parseInt(input.value || input.getAttribute('value') || '0', 10) || 0;

        if (currentQty === actualQty) return true;

        input.value = actualQty;
        input.setAttribute('value', actualQty);
        input.dataset.cartQuantity = actualQty;
        this.quantity?.validateQtyRules?.();

        return true;
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      getStoreMax(input) {
        if (!input?.dataset?.max) return null;
        const storeMax = parseInt(input.dataset.max, 10);
        return Number.isFinite(storeMax) ? storeMax : null;
      }

      getInventoryMax(input) {
        if (
          !input?.dataset?.available ||
          input.dataset.inventoryPolicy === 'continue'
        ) {
          return null;
        }

        const inventoryMax = parseInt(input.dataset.available, 10);
        return Number.isFinite(inventoryMax) ? inventoryMax : null;
      }

      getEffectiveMax(input) {
        const limits = [this.getStoreMax(input), this.getInventoryMax(input)].filter(
          (limit) => limit !== null
        );

        return limits.length ? Math.min(...limits) : null;
      }

      capQuantityInput(input, originalValue) {
        if (!Number.isFinite(originalValue)) return 0;

        const effectiveMax = this.getEffectiveMax(input);
        let value = originalValue;

        if (effectiveMax !== null && value > effectiveMax) {
          value = effectiveMax;
          input.value = value;
          input.dataset.pendingRequestedQty = originalValue;
        } else {
          delete input.dataset.pendingRequestedQty;
        }

        return value;
      }

      getQuantityLimitMessage(input, requestedQty, actualQty) {
        if (requestedQty === actualQty) return '';

        const messages = [];
        const storeMax = this.getStoreMax(input);
        const inventoryMax = this.getInventoryMax(input);

        if (storeMax !== null && requestedQty > storeMax) {
          messages.push(window.cartStrings.quantityError.replace('[quantity]', storeMax));
        }

        if (inventoryMax !== null && requestedQty > inventoryMax) {
          messages.push(window.cartStrings.inventoryError.replace('[quantity]', inventoryMax));
        }

        if (!messages.length) {
          messages.push(window.cartStrings.quantityError.replace('[quantity]', actualQty));
        }

        return messages.join(' ');
      }

      validateQuantity(event) {
        const input = event.target;
        const index = input.dataset.index;
        const originalValue = parseInt(input.value, 10);
        const inputValue = this.capQuantityInput(input, originalValue);

        if (Number.isNaN(inputValue)) return;

        const min = parseInt(input.dataset.min, 10);
        const step = parseInt(input.step, 10);

        if (inputValue < min) {
          this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', min));
          return;
        }

        if (inputValue % step !== 0) {
          this.setValidity(
            event,
            index,
            window.quickOrderListStrings.step_error.replace('[step]', step)
          );
          return;
        }

        this.lastActiveInputId = index;
        input.setCustomValidity('');
        input.reportValidity();
        input.setAttribute('value', inputValue);
        this.startQueue(index, inputValue);
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId =
            this.closest('.collection-quick-add-bulk')?.dataset?.id ||
            this.closest('product-recommendations')?.dataset?.sectionId ||
            this.closest('[data-id]')?.dataset?.id ||
            this.dataset.sectionId;
        }

        return this._sectionId;
      }

      startQueue(id, quantity) {
        this.pendingUpdates[parseInt(id)] = quantity;

        if (this.requestStarted) return;

        if (this.flushQueueTimer) clearTimeout(this.flushQueueTimer);
        this.flushQueueTimer = setTimeout(() => {
          this.flushQueueTimer = null;
          this.flushQueue();
        }, BulkAdd.ASYNC_REQUEST_DELAY);
      }

      flushQueue() {
        if (this.requestStarted) return;
        if (!Object.keys(this.pendingUpdates).length) return;

        const items = { ...this.pendingUpdates };
        this.pendingUpdates = {};
        this.setRequestStarted(true);
        this.updateMultipleQty(items);
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(this.buildSectionRefreshUrl())
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      /**
       * Page URL for cart `sections_url` and for parity with the storefront.
       * Must preserve query string (e.g. `/search?q=…`, filters, sort) or section
       * HTML re-render returns empty / wrong grid and bulk qty never syncs.
       */
      getSectionsUrl() {
        return `${window.location.pathname}${window.location.search}`;
      }

      /** Same as current page query + `section_id` (valid single `?` / `&` chain). */
      buildSectionRefreshUrl() {
        const params = new URLSearchParams(window.location.search);
        params.set('section_id', this.sectionId);

        const productRecommendations = this.closest('product-recommendations');
        if (productRecommendations?.dataset?.productId) {
          params.set('product_id', productRecommendations.dataset.productId);
        }

        return `${window.location.pathname}?${params.toString()}`;
      }

      updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');

        const requestedItems = { ...items };
        const cappedItems = {};

        Object.entries(items).forEach(([variantId, newQtyRaw]) => {
          const input = this.querySelector(`#Quantity-${variantId}`);
          let newQty = Number(newQtyRaw) || 0;

          if (input) {
            newQty = this.capQuantityInput(input, newQty);
          }

          cappedItems[variantId] = newQty;
        });

        const sections = this.getSectionsToRender().map((section) => section.section);
        const sectionsUrl = this.getSectionsUrl();

        const addItems = [];
        const updateItems = {};

        Object.entries(cappedItems).forEach(([variantId, newQty]) => {
          const input = this.querySelector(`#Quantity-${variantId}`);
          const currentQty = parseInt(input?.dataset?.cartQuantity ?? '0', 10);
          if (newQty > currentQty) {
            addItems.push({ id: parseInt(variantId, 10), quantity: newQty - currentQty });
          } else {
            updateItems[variantId] = newQty;
          }
        });

        const fetchIncrease = async () => {
          try {
            const response = await fetch(routes.cart_add_url, {
              ...fetchConfig('javascript'),
              body: JSON.stringify({ items: addItems, sections, sections_url: sectionsUrl }),
            });
            await response.json();
          } catch (error) {
            console.error(error);
          }

          return this.refreshCartUi(sections, sectionsUrl);
        };

        const fetchDecrease = async () => {
          const response = await fetch(routes.cart_update_url, {
            ...fetchConfig(),
            body: JSON.stringify({ updates: updateItems, sections, sections_url: sectionsUrl }),
          });
          const parsedState = JSON.parse(await response.text());

          if (parsedState?.sections?.['cart-drawer'] && Array.isArray(parsedState.items)) {
            return parsedState;
          }

          return this.refreshCartUi(sections, sectionsUrl);
        };

        const hasAdds = addItems.length > 0;
        const hasUpdates = Object.keys(updateItems).length > 0;
        const fetchOp =
          hasAdds && !hasUpdates
            ? fetchIncrease()
            : !hasAdds && hasUpdates
              ? fetchDecrease()
              : fetchIncrease().then(() => fetchDecrease());

        fetchOp
          .then((parsedState) => {
            this.renderSections(parsedState, Object.keys(requestedItems));
            this.syncRequestedQuantities(requestedItems, parsedState);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch((error) => {
            console.error(error);
            Object.keys(requestedItems).forEach((variantId) => {
              this.resetQuantityInput(variantId);
            });
            const input = this.input;
            if (input) {
              input.setCustomValidity(error.message || window.cartStrings.error);
              input.reportValidity();
              this.cleanErrorMessageOnType({ target: input });
            }
          })
          .finally(() => {
            this.selectProgressBar().classList.add('hidden');
            this.setRequestStarted(false);
            this.flushQueue();
          });
      }

      async refreshCartUi(sections, sectionsUrl) {
        const cart = await fetch(`${routes.cart_url}.js`).then((res) => res.json());
        const updates = {};

        cart.items.forEach((item) => {
          updates[item.variant_id] = item.quantity;
        });

        const response = await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ updates, sections, sections_url: sectionsUrl }),
        });

        const parsedState = JSON.parse(await response.text());

        return {
          ...parsedState,
          items: cart.items,
          item_count: cart.item_count,
        };
      }

      syncRequestedQuantities(requestedItems, parsedState) {
        if (!parsedState || !Array.isArray(parsedState.items)) return;

        Object.entries(requestedItems).forEach(([variantId, requestedQtyRaw]) => {
          const requestedQty = Number(requestedQtyRaw) || 0;
          const variantIdNum = Number(variantId);
          const cartLine = parsedState.items.find((item) => {
            const itemVariantId = Number(item.variant_id ?? item.id);
            return itemVariantId === variantIdNum;
          });
          const actualQty = cartLine ? Number(cartLine.quantity) || 0 : 0;
          const input = this.querySelector(`#Quantity-${variantId}`);
          if (!input) return;

          input.value = actualQty;
          input.setAttribute('value', actualQty);
          input.dataset.cartQuantity = actualQty;
          this.quantity?.validateQtyRules?.();

          const pendingRequestedQty = input.dataset.pendingRequestedQty
            ? Number(input.dataset.pendingRequestedQty)
            : requestedQty;
          delete input.dataset.pendingRequestedQty;

          const message = this.getQuantityLimitMessage(input, pendingRequestedQty, actualQty);

          if (message) {
            input.setCustomValidity(message);
            input.reportValidity();
            this.cleanErrorMessageOnType({ target: input });
          } else {
            input.setCustomValidity('');
          }
        });
      }

      getSectionInnerHTML(html, selector) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector(selector);
        return node ? node.innerHTML : null;
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const items = parsedState.items || [];

        this.getSectionsToRender().forEach((section) => {
          const sectionHTML = parsedState.sections?.[section.section];
          if (!sectionHTML) return;

          const sectionElement = document.getElementById(section.id);
          if (!sectionElement) return;

          if (section.section === 'cart-drawer') {
            const cartDrawer = sectionElement.closest('cart-drawer');
            cartDrawer?.classList.toggle('is-empty', items.length === 0);

            if (items.length > 0) {
              cartDrawer?.classList.remove('is-empty');
              sectionElement.querySelector('.drawer__inner-empty')?.remove();
            }

            const drawerInner = sectionElement.querySelector('.drawer__inner');
            const nextHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
            if (drawerInner && nextHTML !== null) {
              drawerInner.innerHTML = nextHTML;
            }
            return;
          }

          const elementToReplace =
            sectionElement.querySelector(section.selector) || sectionElement;
          const nextHTML = this.getSectionInnerHTML(sectionHTML, section.selector);

          if (elementToReplace && nextHTML !== null) {
            elementToReplace.innerHTML = nextHTML;
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`)?.select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
